"use client";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-1 focus-visible:ring-md-primary/30 aria-invalid:border-md-error",
    {
        variants: {
            variant: {
                default:
                    "bg-md-primary text-md-on-primary relative hover:after:bg-white/[var(--color-md-state-hover-opacity)] active:after:bg-white/[var(--color-md-state-pressed-opacity)] after:absolute after:inset-0 after:rounded-[inherit] after:content-[''] after:pointer-events-none",
                destructive:
                    "bg-md-error text-md-on-error hover:bg-md-error/90 focus-visible:ring-md-error/30",
                success:
                    "bg-md-tertiary text-md-on-tertiary hover:bg-md-tertiary/90 focus-visible:ring-md-tertiary/30",
                warning:
                    "bg-amber-600 text-white hover:bg-amber-600/90 focus-visible:ring-amber-600/30",
                info: "bg-md-tertiary text-md-on-tertiary hover:bg-md-tertiary/90 focus-visible:ring-md-tertiary/30",
                outline:
                    "border border-md-outline bg-md-surface hover:bg-md-tertiary-container/20 hover:text-md-on-tertiary-container",
                secondary:
                    "bg-md-secondary-container text-md-on-secondary-container hover:bg-md-secondary-container/80",
                tertiary:
                    "bg-md-tertiary text-md-on-tertiary hover:bg-md-tertiary/90",
                "primary-container":
                    "bg-md-primary-container text-md-on-primary-container hover:bg-md-primary-container/90",
                "secondary-container":
                    "bg-md-secondary-container text-md-on-secondary-container hover:bg-md-secondary-container/90",
                ghost: "hover:bg-md-tertiary-container/10 hover:text-md-on-tertiary-container",
                link: "text-md-primary underline-offset-4 hover:underline",
            },
            size: {
                // default: "h-10 px-4 py-2 has-[>svg]:px-3",
                // sm: "h-9 rounded-sm gap-1 px-3 has-[>svg]:px-2.5",
                // lg: "h-11 rounded-lg px-6 has-[>svg]:px-4",
                // icon: "size-10",
                default:
                    "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
                xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
                sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
                lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
                icon: "size-10",
                "icon-xs":
                    "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
                "icon-sm":
                    "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
                "icon-lg": "size-9",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

function Button({
    className,
    variant = "default",
    size = "default",
    ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
    return (
        <ButtonPrimitive
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}
export { Button, buttonVariants };
